import type { InputHTMLAttributes } from "react";
import { cn } from "@shared/utils";
import { Text } from "@components/atoms/Text";

interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Label shown above the input */
  label?: string;
  /** Whether the input is in edit mode (enabled) or view mode (disabled) */
  variant?: "form" | "editable" | "disabled-view";
}

/**
 * Input field atom.
 * Used in forms: auth modals, profile page, etc.
 * - form: neutral-10 bg, neutral-20 border (for auth modals)
 * - editable: white bg, neutral-20 border, black text (for profile edit mode)
 * - disabled-view: neutral-70 bg, no border, neutral-20 text
 */
export function InputField({
  label,
  variant = "form",
  className,
  ...props
}: InputFieldProps) {
  const isView = variant === "disabled-view";

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <Text variant="h5" as="label" className="font-semibold text-black">
          {label}
        </Text>
      )}
      <input
        className={cn(
          "h-[50px] w-full rounded-lg px-4 text-body outline-none transition-colors",
          isView
            ? "bg-neutral-70 text-neutral-20 cursor-not-allowed"
            : variant === "editable"
              ? "bg-white border border-neutral-20 text-black placeholder:text-neutral-70"
              : "bg-neutral-10 border border-neutral-20 text-black placeholder:text-neutral-70",
          className,
        )}
        disabled={isView}
        {...props}
      />
    </div>
  );
}
